import { Sidebar } from '@/components/common/sidebar';
import { Outlet, LoaderFunctionArgs, ShouldRevalidateFunction } from 'react-router';
import { clubApi } from '@repo/api-client';
import type { ClubDetail } from '@repo/shared-types';
import { useCurrentClub } from '@/hooks/use-current-club';
import { useRouteHeader } from '@repo/ui/hooks/use-route-header';
import { AppShell } from '@repo/ui/components/app-shell';
import { GraceBanner } from '@/components/config/subscription-access';
import { useEffect } from 'react';

function AppLayout() {
  const club = useCurrentClub();
  const { title, backTo } = useRouteHeader();

  // Coming back from another layout (Modo caja, login) is a fresh match, not a
  // revalidation, so shouldRevalidate never drops the club cached before leaving.
  useEffect(
    () => () => {
      clubRequest = null;
    },
    []
  );

  return (
    <AppShell sidebar={<Sidebar club={club} />} title={title} backTo={backTo}>
      <GraceBanner club={club} />
      <Outlet />
    </AppShell>
  );
}

let clubRequest: { clubId: string; club: Promise<ClubDetail> } | null = null;
let reloadOnNavigation = false;

/**
 * The club this layout shows, for a child loader that needs it instead of
 * fetching it again. A navigation that reloads the layout hands over the new one.
 */
export const loadCurrentClub = (clubId: string) => {
  if (clubRequest?.clubId !== clubId) {
    clubRequest = { clubId, club: clubApi.getById(clubId) as Promise<ClubDetail> };
  }
  return clubRequest.club;
};

/** For a change to the club followed by a navigation, which then reloads it. */
export const reloadClubOnNextNavigation = () => {
  reloadOnNavigation = true;
};

const loader = async ({ params }: LoaderFunctionArgs) => {
  const [club, clubs] = await Promise.all([
    loadCurrentClub(params.id as string),
    clubApi.getAll(),
  ]);
  return { club, clubs: clubs.results };
};

// Search-param navigation (pagination, filters) doesn't refetch the club. A
// useRevalidator() call after a mutation keeps the URL and does, so the menu's
// lowStockCount follows sales and stock changes. The cached club is dropped
// here, before any loader of the navigation runs.
const shouldRevalidate: ShouldRevalidateFunction = ({
  currentParams,
  nextParams,
  currentUrl,
  nextUrl,
}) => {
  const reload =
    reloadOnNavigation || currentParams.id !== nextParams.id || currentUrl.href === nextUrl.href;
  if (reload) {
    clubRequest = null;
    reloadOnNavigation = false;
  }
  return reload;
};

export const id = 'app-layout';

export const route = {
  id,
  element: <AppLayout />,
  loader,
  shouldRevalidate,
};
